import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays } from "lucide-react";
import { Input } from "../../components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";
import { useBooking } from "@/context/BookingContext";

const TXT = {
  pt: { title: "Seu agendamento", status: { pending: "Aguardando confirmação", confirmed: "Confirmado", cancelled: "Cancelado", completed: "Realizado" },
    cancel: "Cancelar o agendamento", confirm_cancel: "Confirmar o cancelamento?", yes: "Sim, cancelar", no: "Voltar",
    reschedule: "Pedir outro horário", pick_date: "Escolha uma nova data", no_slots: "Nenhum horário disponível nesta data.",
    send: "Enviar o pedido", cancelled: "Seu agendamento foi cancelado.", rescheduled: "Pedido de remarcação enviado! Você receberá um e-mail quando for confirmado.",
    rebook: "Agendar um novo horário", locked: "Este agendamento não pode mais ser modificado.", notfound: "Agendamento não encontrado." },
  fr: { title: "Votre rendez-vous", status: { pending: "En attente de confirmation", confirmed: "Confirmé", cancelled: "Annulé", completed: "Effectué" },
    cancel: "Annuler le rendez-vous", confirm_cancel: "Confirmer l'annulation ?", yes: "Oui, annuler", no: "Retour",
    reschedule: "Demander un autre créneau", pick_date: "Choisissez une nouvelle date", no_slots: "Aucun créneau disponible à cette date.",
    send: "Envoyer la demande", cancelled: "Votre rendez-vous a été annulé.", rescheduled: "Demande de changement envoyée ! Vous recevrez un e-mail dès sa confirmation.",
    rebook: "Réserver un nouveau créneau", locked: "Ce rendez-vous ne peut plus être modifié.", notfound: "Rendez-vous introuvable." },
};
const fmt = (d) => d.split("-").reverse().join("/");
const dark = "w-full rounded-[2px] bg-[#090708] text-[#E8C47A] py-3.5 text-[11px] tracking-[0.2em] uppercase disabled:opacity-30";
const light = "w-full rounded-[2px] border border-[#090708]/20 py-3.5 text-[11px] tracking-[0.2em] uppercase";

const SlotPicker = ({ t, onSubmit, busy }) => {
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  useEffect(() => { if (date) { setSlot(null); api.get(`/public/availability/slots?date=${date}`).then((r) => setSlots(r.data)).catch(() => setSlots([])); } }, [date]);
  return (
    <div className="space-y-4" data-testid="reschedule-picker">
      <p className="text-[11px] tracking-widest uppercase text-[#090708]/60">{t.pick_date}</p>
      <Input type="date" min={new Date().toISOString().split("T")[0]} value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none border-[#090708]/20" data-testid="reschedule-date-input" />
      {date && (slots.length === 0 ? <p className="text-sm text-[#090708]/50" data-testid="reschedule-no-slots">{t.no_slots}</p> : (
        <div className="grid grid-cols-2 gap-2">
          {slots.map((s) => (
            <button key={s.slot} disabled={!s.available} onClick={() => setSlot(s.slot)} data-testid={`reschedule-slot-${s.slot}`}
              className={`rounded-[2px] px-3 py-2.5 text-sm border transition-colors ${!s.available ? "opacity-30 cursor-not-allowed border-[#090708]/10" : slot === s.slot ? "bg-[#090708] text-[#E8C47A] border-[#090708]" : "border-[#090708]/20 hover:border-[#090708]"}`}>{s.slot}</button>
          ))}
        </div>
      ))}
      <button disabled={!slot || busy} onClick={() => onSubmit(date, slot)} className={dark} data-testid="reschedule-submit">{t.send}</button>
    </div>
  );
};

export default function ManageAppointment() {
  const { token } = useParams();
  const { lang, setLang } = useLanguage();
  const { openBooking } = useBooking();
  const [appt, setAppt] = useState(undefined);
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const t = TXT[lang] || TXT.pt;

  useEffect(() => {
    api.get(`/public/appointments/manage/${token}`).then((r) => { setAppt(r.data); if (r.data.lang !== lang) setLang(r.data.lang); }).catch(() => setAppt(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const act = async (path, body, key) => {
    setBusy(true);
    try { const { data } = await api.post(`/public/appointments/manage/${token}/${path}`, body); setAppt({ ...appt, ...data }); setDone(key); setMode(null); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (appt === undefined) return <div className="min-h-[60vh]" />;
  if (appt === null) return <p className="pt-40 pb-24 text-center text-[#090708]/60" data-testid="manage-not-found">{t.notfound}</p>;
  const locked = appt.is_past || ["cancelled", "completed"].includes(appt.status);

  return (
    <section className="pt-36 pb-24 px-6 max-w-md mx-auto min-h-[80vh]" data-testid="manage-appointment-page">
      <p className="text-[11px] tracking-[0.25em] uppercase text-[#9B6179] mb-2">{t.title}</p>
      <h1 className="font-serif text-3xl text-[#090708] mb-6">{lang === "pt" ? appt.service_name_pt : appt.service_name_fr}</h1>
      <div className="border-y border-[#090708]/10 py-5 mb-6 flex items-center gap-3">
        <CalendarDays className="w-5 h-5 text-[#9B6179]" />
        <div><p className="font-serif text-xl" data-testid="manage-appointment-when">{fmt(appt.date)} — {appt.slot}</p>
          <p className="text-xs tracking-widest uppercase text-[#090708]/50" data-testid="manage-appointment-status">{t.status[appt.status]}</p></div>
      </div>
      {done && <p className="mb-6 text-sm text-[#6F3F55]" data-testid="manage-done-message">{done === "cancel" ? t.cancelled : t.rescheduled}</p>}
      {locked ? (
        <div className="space-y-3">
          {!done && <p className="text-sm text-[#090708]/50" data-testid="manage-locked">{t.locked}</p>}
          <button onClick={() => openBooking()} className={dark} data-testid="manage-rebook">{t.rebook}</button>
        </div>
      ) : mode === "cancel" ? (
        <div className="space-y-3" data-testid="manage-cancel-confirm">
          <p className="font-serif text-lg">{t.confirm_cancel}</p>
          <button disabled={busy} onClick={() => act("cancel", {}, "cancel")} className={`${dark} !bg-[#6F3F55]`} data-testid="manage-cancel-yes">{t.yes}</button>
          <button onClick={() => setMode(null)} className={light} data-testid="manage-cancel-no">{t.no}</button>
        </div>
      ) : mode === "reschedule" ? (
        <div className="space-y-3"><SlotPicker t={t} busy={busy} onSubmit={(date, slot) => act("reschedule", { date, slot }, "reschedule")} />
          <button onClick={() => setMode(null)} className={light} data-testid="reschedule-back">{t.no}</button></div>
      ) : (
        <div className="space-y-3">
          <button onClick={() => setMode("reschedule")} className={dark} data-testid="manage-reschedule-button">{t.reschedule}</button>
          <button onClick={() => setMode("cancel")} className={light} data-testid="manage-cancel-button">{t.cancel}</button>
        </div>
      )}
    </section>
  );
}
